import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { Image } from '@rocco/components/image';
import { DetailSaveButton } from '~/ui-rocco/pages/detail-shared';
import { ISLAND_GLASS_LIGHT } from '~/ui-rocco/pages/detail-shared/magic-island/styles';

import { RevampShareButton } from './RevampShareButton';
import { pickSpeechVoice } from '../util/pick-speech-voice';

interface ArticleReadAloudBarProps {
    /** The article's own path, for the share control. */
    shareLink: string;
    articleID: number;
    articleTitle: string;
    /** Banner still, shown as the bar's thumbnail. */
    thumbnailUrl?: string;
    /** The article's opening paragraph, read after the headline. */
    intro?: string;
    /**
     * Element holding the article's prose. Read at play time rather than
     * passed as a string: the body arrives as page-builder modules, and the
     * rendered text is the only place it exists as continuous copy.
     */
    bodySelector?: string;
}

/** Speech runs near this rate, which is what turns a word count into a clock. */
const WORDS_PER_SECOND = 2.5;

/**
 * A touch under the default. The platform default is pitched for menus and
 * notifications, and reads long prose faster than anyone speaks it.
 */
const SPEECH_RATE = 0.95;

/**
 * Chrome stops speaking after roughly 15 seconds unless the queue is nudged.
 * A resume() on a running synthesiser is a no-op, so this is safe to keep
 * ticking for the whole reading.
 */
const KEEPALIVE_MS = 10000;

/** Bar heights of the waveform glyph, as fractions of its box. */
const WAVE_BARS = [0.35, 0.62, 1, 0.78, 1, 0.55, 0.3];

/** The elements that hold an article's prose, in reading order. */
const PROSE_SELECTOR = 'p, h2, h3, h4, blockquote, li';

/** Chrome that sits among the prose but is not part of the article. */
const NOT_PROSE =
    'button, nav, header, footer, figcaption, [aria-hidden="true"]';

/**
 * Pulls the article's prose out of the rendered page, in order.
 *
 * The body arrives as page-builder modules, so the rendered DOM is the only
 * place it exists as continuous copy — but that DOM also holds the module CTAs,
 * the tag pills and the image captions, and a reading that recites "View
 * Profile Contact" is worse than no reading. So this takes the prose elements
 * and steps around everything else.
 */
const collectProse = (root: Element): string[] => {
    const blocks: string[] = [];
    const taken: Element[] = [];

    root.querySelectorAll(PROSE_SELECTOR).forEach((element) => {
        if (element.closest(NOT_PROSE)) return;
        // A nested match (a <p> inside an <li>) is already covered by the
        // ancestor that was taken, and reading both would say it twice.
        if (taken.some((previous) => previous.contains(element))) return;

        const text = (element.textContent ?? '').replace(/\s+/g, ' ').trim();
        if (!text) return;

        // An element that is nothing but a link is navigation — a tag pill or
        // a CTA — not a sentence of the article.
        const link = element.querySelector('a');
        if (link && (link.textContent ?? '').trim() === text) return;

        taken.push(element);
        blocks.push(text);
    });

    return blocks;
};

/** One sentence of the reading, and where it falls on the clock. */
interface Segment {
    text: string;
    /** Seconds from the start of the article to the start of this sentence. */
    startsAt: number;
}

const formatClock = (totalSeconds: number) => {
    const safe = Math.max(0, Math.round(totalSeconds));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

/**
 * Splits the article's blocks into sentences and gives each a start time.
 *
 * Sentences are the unit the whole player works in: each is spoken as its own
 * utterance, which is what gives the reading its pauses, and it is also the
 * finest place the scrubber can seek to — the speech engines expose no way to
 * start partway through one. Blocks are kept apart so a headline, which carries
 * no full stop, is still its own sentence rather than running into the first
 * line of the article.
 */
const toSegments = (blocks: string[]): Segment[] => {
    const segments: Segment[] = [];
    let cursor = 0;

    for (const block of blocks) {
        const sentences = block.match(/[^.!?]+[.!?]*\s*/g) ?? [block];
        for (const sentence of sentences) {
            const text = sentence.trim();
            if (!text) continue;
            segments.push({ text, startsAt: cursor });
            cursor += text.split(/\s+/).length / WORDS_PER_SECOND;
        }
    }

    return segments;
};

/**
 * Reads the article aloud, in the Magic Island's band.
 *
 * The browser's own speech synthesiser does the reading, so there is no audio
 * file to produce per article and nothing to keep in sync when an editor edits
 * one — the trade is that the voice is the reader's own device voice, and that
 * the clock is an estimate from word counts rather than a real duration.
 *
 * Laid out as a collapsed player: the control, the clock, and the article's own
 * picture, with the reader's two actions at the far end. Idle it shows a
 * waveform; once reading, that gives way to the scrubber.
 */
export const ArticleReadAloudBar = ({
    shareLink,
    articleID,
    articleTitle,
    thumbnailUrl,
    intro,
    bodySelector = '[data-article-body]',
}: ArticleReadAloudBarProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [hasStarted, setHasStarted] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isSupported, setIsSupported] = useState(true);
    const [voice, setVoice] = useState<SpeechSynthesisVoice | null>(null);
    const segmentsRef = useRef<Segment[]>([]);
    /**
     * Which run of the reading is current. `cancel()` delivers `error` (and on
     * some engines `end`) to every utterance still queued from the run being
     * replaced, asynchronously — so without this a seek's cancel lands after
     * the new run has started and reports the reading as stopped while it is
     * still going. Handlers check their run against this and stale ones do
     * nothing.
     */
    const runIdRef = useRef(0);
    /** The sentence being spoken, so a stop can be resumed from where it was. */
    const resumeIndexRef = useRef(0);

    useEffect(() => {
        setIsSupported(
            typeof window !== 'undefined' && 'speechSynthesis' in window,
        );
    }, []);

    // The voice list is populated asynchronously, and on Chrome is empty on the
    // first call — so read it now and again when the browser says it changed.
    useEffect(() => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
            return;
        }
        const synth = window.speechSynthesis;
        const sync = () => setVoice(pickSpeechVoice(synth.getVoices()));
        sync();
        synth.addEventListener('voiceschanged', sync);
        return () => synth.removeEventListener('voiceschanged', sync);
    }, []);

    // Stop speaking if the reader leaves this article: the synthesiser is a
    // window singleton and would otherwise keep reading over the next page.
    //
    // Keyed on the article, not just on unmount. Article to article is a param
    // change on the same `/article/$slug` route, so this component never
    // unmounts between them — an unmount-only cleanup never ran, and the
    // reader arrived at the next article with the previous one still being
    // read aloud, the island showing Pause and a clock whose length belonged
    // to the article they had left.
    useEffect(
        () => () => {
            if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
                // Retire the run first, so the cancel's own events cannot set
                // state after this article is behind us.
                runIdRef.current += 1;
                window.speechSynthesis.cancel();
            }
        },
        [articleID],
    );

    // The run is gone with the article; so is everything that described it.
    // Segments are re-read from the new page's DOM at play time.
    useEffect(() => {
        segmentsRef.current = [];
        resumeIndexRef.current = 0;
        setIsPlaying(false);
        setHasStarted(false);
        setElapsed(0);
        setDuration(0);
    }, [articleID]);

    useEffect(() => {
        if (!isPlaying) return;
        const timer = window.setInterval(
            () => setElapsed((seconds) => seconds + 1),
            1000,
        );
        // See KEEPALIVE_MS — without this the reading stops partway through a
        // long article on Chrome, with no error and no `end` event. Safe now
        // that stopping cancels: on a synthesiser that is not paused this is a
        // no-op, where before it could un-pause a reading the reader had just
        // stopped.
        const beat = window.setInterval(() => {
            window.speechSynthesis.resume();
        }, KEEPALIVE_MS);
        return () => {
            window.clearInterval(timer);
            window.clearInterval(beat);
        };
    }, [isPlaying]);

    /** Reads the article out of the page and splits it, once per reading. */
    const loadSegments = useCallback(() => {
        if (segmentsRef.current.length > 0) return segmentsRef.current;

        const body = document.querySelector(bodySelector);
        // Headline first, then the standfirst, then the article — the order a
        // reader meets them, and the order that tells a listener what they have
        // put on before it starts.
        const blocks = [
            articleTitle,
            intro,
            ...(body ? collectProse(body) : []),
        ].filter((block): block is string => !!block?.trim());

        if (blocks.length === 0) return [];

        const segments = toSegments(blocks);
        segmentsRef.current = segments;

        const last = segments[segments.length - 1];
        if (last) {
            setDuration(
                last.startsAt +
                    last.text.split(/\s+/).length / WORDS_PER_SECOND,
            );
        }
        return segments;
    }, [articleTitle, bodySelector, intro]);

    /**
     * Speaks from one sentence to the end. Every start goes through here — the
     * play button and every seek — so there is one path that queues speech and
     * one place the end of the reading is noticed.
     */
    const speakFrom = useCallback(
        (segments: Segment[], index: number) => {
            const synth = window.speechSynthesis;
            // Claim the new run before cancelling, so the events the cancel is
            // about to deliver are already stale by the time they arrive.
            const runId = ++runIdRef.current;
            synth.cancel();

            const rest = segments.slice(index);
            if (rest.length === 0) {
                setIsPlaying(false);
                setHasStarted(false);
                return;
            }

            resumeIndexRef.current = index;
            setHasStarted(true);
            rest.forEach((segment, offset) => {
                const utterance = new SpeechSynthesisUtterance(segment.text);
                if (voice) utterance.voice = voice;
                utterance.rate = SPEECH_RATE;
                utterance.lang = voice?.lang ?? 'en-NZ';
                // The clock follows the speech rather than the timer: every
                // sentence reports where the reading actually is, correcting
                // the drift a per-second estimate accumulates.
                utterance.onstart = () => {
                    if (runIdRef.current !== runId) return;
                    resumeIndexRef.current = index + offset;
                    setElapsed(segment.startsAt);
                };
                const finish = () => {
                    if (runIdRef.current !== runId) return;
                    setIsPlaying(false);
                    setHasStarted(false);
                };
                if (offset === rest.length - 1) utterance.onend = finish;
                utterance.onerror = finish;
                synth.speak(utterance);
            });

            setIsPlaying(true);
        },
        [voice],
    );

    /**
     * Stops the reading where it stands.
     *
     * Cancels rather than pauses. `pause()` is the call that would keep the
     * place mid-sentence, but no engine implements it the same way: Chrome
     * lets the current utterance play on and leaves the synthesiser paused
     * afterwards, so a later `speak()` queues and never starts; Android Chrome
     * ignores the call outright. The sentence being read is already recorded,
     * so play picks up from the top of it — one behaviour on every engine, and
     * the same place a seek would land.
     *
     * The run is retired before the cancel, so the `error`/`end` events the
     * cancel delivers are already stale and cannot reset state underneath the
     * next play.
     */
    const stop = useCallback(() => {
        runIdRef.current += 1;
        window.speechSynthesis.cancel();
        setIsPlaying(false);
    }, []);

    const onToggle = useCallback(() => {
        if (!isSupported) return;

        // Our own flag, which is also the one the icon reads — so the button
        // cannot do one thing while showing another. It used to ask the engine
        // instead, and around a pause Chrome reports `paused` while the audio
        // is still running: the first press appeared to do nothing and the
        // second resumed rather than stopped.
        if (isPlaying) {
            stop();
            return;
        }

        const segments = loadSegments();
        if (segments.length === 0) return;

        if (!hasStarted) setElapsed(0);
        // Carry on from the sentence that was being read, or open the article
        // from the top when this is the first play.
        speakFrom(segments, hasStarted ? resumeIndexRef.current : 0);
    }, [hasStarted, isPlaying, isSupported, loadSegments, speakFrom, stop]);

    /**
     * Seeks to a point on the clock, to the nearest sentence start.
     *
     * There is no way to start an utterance partway through, so the scrubber
     * lands on the sentence holding the chosen second and begins it again —
     * which is also what a listener wants, rather than resuming mid-word.
     */
    const onSeek = useCallback(
        (seconds: number) => {
            const segments = loadSegments();
            if (segments.length === 0) return;

            const after = segments.findIndex(
                (segment) => segment.startsAt > seconds,
            );
            const index =
                after === -1 ? segments.length - 1 : Math.max(0, after - 1);

            const target = segments[index];
            if (!target) return;
            setElapsed(target.startsAt);
            speakFrom(segments, index);
        },
        [loadSegments, speakFrom],
    );

    // Before the first play the clock shows how long the reading will take;
    // after it, how far in the reader is. A collapsed player has room for one
    // number, and which one is useful changes with the state.
    const clock = useMemo(
        () => formatClock(hasStarted ? elapsed : duration),
        [duration, elapsed, hasStarted],
    );

    const isScrubbable = hasStarted && duration > 0;
    const progress = duration > 0 ? Math.min(1, elapsed / duration) : 0;

    if (!isSupported) return null;

    return (
        <div
            className={cn(
                ISLAND_GLASS_LIGHT,
                'flex h-[4.5rem] items-center gap-[1rem] rounded-[var(--radius-btn-l)] pr-[1.25rem] pl-[0.5rem]',
                // The bar only takes the width the scrubber needs once there is
                // one, so the idle state stays a compact pill.
                isScrubbable && 'w-[41rem] max-w-[92vw]',
            )}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-label={isPlaying ? 'Pause article' : 'Listen to article'}
                aria-pressed={isPlaying}
                className="flex size-[3.5rem] shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-btn-l)] border-0 bg-white text-[#181818] shadow-[0_2px_8px_rgba(0,0,0,0.12)] transition-transform hover:scale-105"
            >
                {isPlaying ? (
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="size-[1.25rem]"
                        fill="currentColor"
                    >
                        <rect x="6" y="4" width="4" height="16" rx="1" />
                        <rect x="14" y="4" width="4" height="16" rx="1" />
                    </svg>
                ) : (
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        className="ml-[0.125rem] size-[1.25rem]"
                        fill="currentColor"
                    >
                        <path d="M8 5.14v13.72L19 12 8 5.14z" />
                    </svg>
                )}
            </button>

            <span className="shrink-0 font-sans text-[1.0625rem] leading-none font-semibold text-[#181818] tabular-nums">
                {clock}
            </span>

            {isScrubbable ? (
                <label className="flex min-w-0 flex-1 items-center">
                    <span className="sr-only">Position in article</span>
                    {/* A real range input rather than a div and a drag handler:
                        it brings keyboard seeking and the pointer handling with
                        it, both of which a hand-rolled track gets wrong. */}
                    <input
                        type="range"
                        min={0}
                        max={Math.round(duration)}
                        step={1}
                        value={Math.min(
                            Math.round(elapsed),
                            Math.round(duration),
                        )}
                        onChange={(event) => onSeek(Number(event.target.value))}
                        className={cn(
                            'h-[0.25rem] w-full cursor-pointer appearance-none rounded-full',
                            // appearance-none takes the native thumb with it,
                            // so both engines' thumbs are drawn back by hand.
                            '[&::-webkit-slider-thumb]:size-[0.875rem] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#181818] [&::-webkit-slider-thumb]:shadow-[0_1px_3px_rgba(0,0,0,0.3)]',
                            '[&::-moz-range-thumb]:size-[0.875rem] [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[#181818]',
                        )}
                        style={{
                            background: `linear-gradient(to right, #181818 ${
                                progress * 100
                            }%, rgba(24,24,24,0.18) ${progress * 100}%)`,
                        }}
                    />
                </label>
            ) : (
                /* Stands for the audio rather than describing it — the
                   synthesiser exposes no amplitude to draw. */
                <span
                    aria-hidden="true"
                    className="flex h-[1.25rem] shrink-0 items-center gap-[0.1875rem]"
                >
                    {WAVE_BARS.map((height, index) => (
                        <span
                            key={index}
                            className="w-[0.125rem] rounded-full bg-[#181818]/70"
                            style={{ height: `${height * 100}%` }}
                        />
                    ))}
                </span>
            )}

            {thumbnailUrl ? (
                <span className="relative size-[3rem] shrink-0 overflow-hidden rounded-[0.625rem]">
                    <Image
                        src={thumbnailUrl}
                        alt=""
                        fill={true}
                        fit="cover"
                        sizes="48px"
                        className="absolute inset-0"
                    />
                </span>
            ) : null}

            {isScrubbable ? null : <span className="min-w-[2rem] flex-1" />}

            <span className="flex shrink-0 items-center gap-[0.25rem]">
                <RevampShareButton
                    shareLink={shareLink}
                    articleID={articleID}
                    articleTitle={articleTitle}
                    iconOnly
                    image={thumbnailUrl}
                    sheetPlacement="above"
                    className="size-[2.5rem] border-0 bg-transparent px-0 shadow-none hover:bg-black/5"
                />
                {/* Ink is pinned dark because the island drops the plate: the
                    saved state's own white ink only reads against the dark
                    backplate `bg-transparent` removes, so saved renders as a
                    filled dark bookmark on the glass instead of white on white. */}
                <DetailSaveButton
                    pinId={articleID}
                    pinType="ArticlePin"
                    className="size-[2.5rem]! border-0 bg-transparent text-[#181818] hover:bg-black/5"
                />
            </span>
        </div>
    );
};
